<template>
  <div class="preorder-history-section">
    <div class="preorder-history-compact mt-3">
      <div class="preorder-history-compact__header">
        <i class="fa fa-history mr-2"></i>
        <span>Lịch sử</span>
      </div>
      <div class="preorder-history-compact__body">
        <div v-if="loading" class="text-center py-2">
          <b-spinner small></b-spinner>
        </div>
        <template v-else>
          <div
            v-for="(item, idx) in histories"
            :key="item.id || idx"
            class="preorder-history-compact__row"
            @click="openDetail(item)"
          >
            <div class="preorder-history-compact__left">
              <span class="font-weight-medium">{{
                item.creator ? item.creator.fullName : '—'
              }}</span>
              <span class="d-block small text-muted">{{
                item.createdAtDate
              }}</span>
              <span class="d-block small text-muted">{{
                item.createdAtTime
              }}</span>
            </div>
            <div class="preorder-history-compact__right text-right">
              <span class="text-primary cursor-pointer">
                {{ getActionText(item.action) }}
              </span>
            </div>
          </div>
          <p
            v-if="!loading && histories.length === 0"
            class="text-muted small mb-0 py-2"
          >
            Chưa có lịch sử
          </p>
        </template>
      </div>
    </div>

    <b-modal
      v-model="showModal"
      title="Thông tin thay đổi"
      size="xl"
      ok-only
      ok-title="Đóng"
      @hide="
        selectedItem = null;
        detailLoading = false;
      "
    >
      <div v-if="detailLoading" class="text-center py-4">
        <b-spinner></b-spinner>
        <p class="mt-2 mb-0">Đang tải chi tiết...</p>
      </div>
      <div v-else-if="selectedItem" class="history-diff">
        <table class="table table-bordered table-sm mb-0">
          <thead class="bg-light">
            <tr>
              <th style="width: 25%">Thông tin trên phiếu</th>
              <th style="width: 37%">Trước khi sửa</th>
              <th style="width: 38%">Sau khi sửa</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="line in mainDiffLines" :key="line.key">
              <td>{{ line.label }}</td>
              <td>
                {{ line.old !== undefined && line.old !== '' ? line.old : '—' }}
              </td>
              <td>
                {{ line.new !== undefined && line.new !== '' ? line.new : '—' }}
              </td>
            </tr>
          </tbody>
        </table>
        <p v-if="mainDiffLines.length === 0" class="text-muted mb-0 mt-2">
          Không có thay đổi chi tiết.
        </p>

        <template v-if="tradeinDiffLines.length > 0">
          <h5 class="bold-text mt-4 mb-2">Chi tiết thay đổi sản phẩm thu cũ</h5>
          <hr />
          <table class="table table-bordered table-sm table-hover mb-0">
            <thead class="bg-light">
              <tr>
                <th style="width: 25%">Thông tin</th>
                <th style="width: 37%">Trước</th>
                <th style="width: 38%">Sau</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in tradeinDiffLines" :key="row.key">
                <td>{{ row.label }}</td>
                <td>
                  {{ row.old !== undefined && row.old !== '' ? row.old : '—' }}
                </td>
                <td>
                  {{ row.new !== undefined && row.new !== '' ? row.new : '—' }}
                </td>
              </tr>
            </tbody>
          </table>
        </template>
      </div>
      <p v-else-if="!detailLoading" class="text-muted mb-0">
        Không có dữ liệu chi tiết.
      </p>
    </b-modal>
  </div>
</template>

<script>
import Differify, { PROPERTY_STATUS } from '@netilon/differify';
import PreorderHistoryService from '@/core/api/preorder/preorder-history.service';
import { PREORDER_HISTORY_ACTION_CONST } from '@/utils/constants';
import { PREORDER_STATUS_NAME } from '@/utils/enum-name';
import moment from 'moment';

const FIELD_LABELS = {
  status: 'Trạng thái',
  note: 'Ghi chú',
  storeName: 'Cửa hàng',
  tradeinProductName: 'Sản phẩm thu cũ',
  tradeinChassisNumber: 'Số khung',
  tradeinEngineNumber: 'Số máy',
  tradeinEstimationPrice: 'Giá thu dự kiến',
  targetProductName: 'Sản phẩm muốn mua',
  targetQuotedPrice: 'Giá bán',
  preorderProgramName: 'Chương trình',
};

const TRADEIN_KEYS = [
  'tradeinProductName',
  'tradeinChassisNumber',
  'tradeinEngineNumber',
  'tradeinEstimationPrice',
];

/** Chỉ các key có trong FIELD_LABELS và không thuộc TRADEIN_KEYS mới hiển thị ở bảng chính */
const MAIN_TABLE_KEYS = Object.keys(FIELD_LABELS).filter(
  (k) => !TRADEIN_KEYS.includes(k),
);

/** Lấy object chỉ chứa các key được phép (để so sánh) */
function pickKeys(obj, keys) {
  if (!obj || typeof obj !== 'object' || !Array.isArray(keys) || !keys.length) {
    return {};
  }

  return keys.reduce((acc, key) => {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      acc[key] = obj[key];
    }

    return acc;
  }, {});
}

/** Chuyển giá trị sang chuỗi hiển thị (status map, object → JSON) */
function formatDisplayValue(key, val) {
  if (val === undefined || val === null) return '';

  if (key === 'status') {
    const mapped = PREORDER_STATUS_NAME[val];
    return mapped != null ? mapped : String(val);
  }

  if (typeof val === 'object') {
    try {
      return JSON.stringify(val);
    } catch (e) {
      // Fallback tránh crash UI nếu có circular reference
      return String(val);
    }
  }

  return String(val);
}

/** Duyệt kết quả diff (object phẳng) và trả về mảng { key, label, old, new } */
function diffResultToLines(diff, allowedKeys, fieldLabels) {
  if (
    !diff ||
    !diff._ ||
    typeof diff._ !== 'object' ||
    !Array.isArray(allowedKeys) ||
    !allowedKeys.length
  ) {
    return [];
  }

  const lines = [];

  allowedKeys.forEach((key) => {
    const node = diff._[key];
    const label = fieldLabels[key];

    if (!node || node.status === PROPERTY_STATUS.EQUAL || !label) return;

    lines.push({
      key,
      label,
      old: formatDisplayValue(key, node.original),
      new: formatDisplayValue(key, node.current),
    });
  });

  return lines;
}

export default {
  name: 'PreorderHistorySection',
  props: {
    preorderId: { type: [Number, String], default: null },
  },
  data() {
    return {
      histories: [],
      loading: false,
      showModal: false,
      selectedItem: null,
      detailLoading: false,
      differify: new Differify({ mode: { object: 'DIFF', array: 'DIFF' } }),
    };
  },
  watch: {
    preorderId: {
      immediate: true,
      handler(id) {
        if (id) this.fetch();
        else this.histories = [];
      },
    },
  },
  computed: {
    mainDiffLines() {
      if (!this.selectedItem) return [];
      const oldVal = pickKeys(
        this.selectedItem.originalValue || {},
        MAIN_TABLE_KEYS,
      );
      const newVal = pickKeys(
        this.selectedItem.newValue || {},
        MAIN_TABLE_KEYS,
      );
      const diff = this.differify.compare(oldVal, newVal);
      return diffResultToLines(diff, MAIN_TABLE_KEYS, FIELD_LABELS);
    },
    tradeinDiffLines() {
      if (!this.selectedItem) return [];
      const oldVal = pickKeys(
        this.selectedItem.originalValue || {},
        TRADEIN_KEYS,
      );
      const newVal = pickKeys(this.selectedItem.newValue || {}, TRADEIN_KEYS);
      const diff = this.differify.compare(oldVal, newVal);
      return diffResultToLines(diff, TRADEIN_KEYS, FIELD_LABELS);
    },
  },
  methods: {
    async fetch() {
      const id = this.preorderId;
      if (!id) return;
      this.loading = true;
      try {
        const res = await PreorderHistoryService.getList(id, { getFull: true });
        const list = res?.data?.data ?? res?.data ?? [];
        const arr = Array.isArray(list) ? list : [];
        this.histories = arr.map((item) => {
          const m = item.createdAt ? moment(item.createdAt) : null;
          return {
            ...item,
            createdAtFormatted: m ? m.format('DD/MM/YYYY HH:mm') : '',
            createdAtDate: m ? m.format('DD/MM/YYYY') : '',
            createdAtTime: m ? m.format('HH:mm:ss') : '',
          };
        });
      } catch (e) {
        console.error('Load preorder history failed', e);
        this.histories = [];
      } finally {
        this.loading = false;
      }
    },
    getActionText(action) {
      const found = (PREORDER_HISTORY_ACTION_CONST || []).find(
        (x) => Number(x.value) === Number(action),
      );
      return found ? found.text : '—';
    },
    getStatusText(status) {
      if (status == null) return '';
      return PREORDER_STATUS_NAME[status] != null
        ? PREORDER_STATUS_NAME[status]
        : String(status);
    },
    async openDetail(item) {
      if (!item?.id) return;
      this.showModal = true;
      this.detailLoading = true;
      this.selectedItem = null;
      try {
        const res = await PreorderHistoryService.getById(item.id);
        const raw = res?.data?.data ?? res?.data ?? res;
        this.selectedItem = raw || item;
      } catch (e) {
        console.error('Load preorder history detail failed', e);
        this.selectedItem = item;
      } finally {
        this.detailLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.preorder-history-compact {
  border: 1px solid #e2e5ec;
  border-radius: 4px;
  overflow: hidden;
}
.preorder-history-compact__header {
  background: #eef0f8;
  color: #6c757d;
  padding: 6px 10px;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
}
.preorder-history-compact__body {
  padding: 0;
  max-height: 250px;
  overflow-y: auto;
}
.preorder-history-compact__row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 8px 10px;
  border-bottom: 1px solid #eee;
  cursor: pointer;
}
.preorder-history-compact__row:last-child {
  border-bottom: none;
}
.preorder-history-compact__row:hover {
  background: #f8f9fa;
}
.preorder-history-compact__left {
  flex: 1;
  min-width: 0;
  font-size: 0.875rem;
}
.preorder-history-compact__right {
  flex-shrink: 0;
  margin-left: 8px;
  font-size: 0.875rem;
}
.cursor-pointer {
  cursor: pointer;
}
.history-diff {
  max-height: 60vh;
  overflow-y: auto;
}
.bold-text {
  font-weight: 600;
  color: #212121;
}
</style>
